import Paper from "@material-ui/core/Paper";
import { StyleRules, Theme, withStyles, WithStyles } from "@material-ui/core/styles";
import Downshift from "downshift";
import React, { useState } from "react";
import styled from "styled-components";
import Items from "./Items";
import NotFound from "./NotFound";
import Searching from "./Searching";
import { Suggestion } from "./types";

const Input = styled("input")`
    border: 1px solid $color-gray-10;
    font-size: 12px;
    height: 40px;
    margin: 12px 0 0;
    padding-left: 10px;
    width: 100%;

    ${(props) => props.theme.breakpoints.up("desktop")} {
        font-size: 16px;
        padding-left: 10px;
    }
`;

function renderInput({ ref, ...rest }: any) {
    return <Input type="text" inputRef={ref} {...rest} />;
}

type Styles = "container" | "inputInput" | "inputRoot" | "paper" | "root";

const styles = (theme: Theme): StyleRules<Styles> => ({
    container: {
        flexGrow: 1,
        position: "relative",
    },
    inputInput: {
        flexGrow: 1,
        width: "auto",
    },
    inputRoot: {
        flexWrap: "wrap",
    },
    paper: {
        left: 0,
        marginBottom: theme.spacing.unit * 2,
        marginTop: theme.spacing.unit,
        position: "absolute",
        right: 0,
        zIndex: 1,
    },
    root: {
        flexGrow: 1,
    },
});

interface Props<T> {
    count?: number;
    items: Array<T & Suggestion>;
    placeholder?: string;
    searching?: boolean;
    value?: string;

    onChange?(value: string): void;
    onFocus?(e: React.FocusEvent<HTMLInputElement>): void;
    onSelection?(item: T & Suggestion): void;
}

function Autocomplete<T>(props: Props<T> & WithStyles<Styles>) {
    const [inputValue, setInputValue] = useState("");

    const {
        classes,
        count = props.count || 5,
        items,
        onChange,
        onFocus,
        onSelection,
        placeholder,
        searching,
        value,
    } = props;

    const valueToUse = value || inputValue;

    function handleOnChange({ target: { value: eventValue } }: React.ChangeEvent<HTMLInputElement>) {
        !props.value && setInputValue(eventValue);

        onChange && onChange(eventValue);
    }

    function handleOnSelection(selection: string) {
        onSelection && onSelection(items.find((f) => f.label === selection) as T & Suggestion);
    }

    function handleOnFocus(e: React.FocusEvent<HTMLInputElement>) {
        onFocus && onFocus(e);
    }

    return (
        <div className={classes.root}>
            <Downshift inputValue={valueToUse} onChange={handleOnSelection}>
                {({
                    getInputProps,
                    getItemProps,
                    getMenuProps,
                    highlightedIndex,
                    inputValue: inputValue2,
                    isOpen,
                    selectedItem,
                }) => (
                    <div className={classes.container}>
                        {renderInput(
                            getInputProps({
                                onChange: handleOnChange,
                                onFocus: handleOnFocus,
                                placeholder,
                            })
                        )}
                        <div {...getMenuProps()}>
                            {isOpen ? (
                                <Paper className={classes.paper} square>
                                    {searching ? (
                                        <Searching />
                                    ) : items.length === 0 && valueToUse ? (
                                        <NotFound />
                                    ) : (
                                        <Items
                                            count={count}
                                            highlightedIndex={highlightedIndex}
                                            getItemProps={getItemProps}
                                            items={items}
                                            selectedItem={selectedItem}
                                        />
                                    )}
                                </Paper>
                            ) : null}
                        </div>
                    </div>
                )}
            </Downshift>
        </div>
    );
}

export default withStyles(styles)(Autocomplete);
